/* eslint-env browser */
// Security dashboard › Security logs (wave 3, W2_Portal_Tables look).
//
// Owns its data fetch (limitToLast(100) on /security_logs, once when an
// admin opens the tab) so the tab only costs a read when it is opened.
// The table shows the failed / blocked / suspicious events; a row opens the
// event's details.
//
// Bundles the related admin-only tools of this tab: purge security logs,
// user-data cleanup and the /transactionStats backfill. They use the same
// admin-token fetch as before; the user-data cleanup calls the function's
// direct URL, because a run takes minutes and the Hosting rewrite answers
// 504 after 60 s.
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, query, limitToLast } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { InlineMessage } from '../../components/feedback';
import { tokens as c } from '../../theme';
import { formatAddisDateTime } from '../../common/exportUtils';
import { DataTable, PageHeader, StatusChip, IconCircle, InfoRow, calmCaps } from '../../components/modern';

const getEventSeverity = (eventType) => {
  if (!eventType) return 'low';
  if (eventType.includes('UNAUTHORIZED') || eventType.includes('BLOCKED') || eventType.includes('ATTACK') || eventType.includes('ABUSE')) return 'high';
  if (eventType.includes('FAILED') || eventType.includes('RATE_LIMIT') || eventType.includes('MISMATCH') || eventType.includes('ERROR') || eventType.includes('EXCEEDED') || eventType.includes('EXPIRED') || eventType.includes('INVALID')) return 'medium';
  return 'low';
};

const isNegativeOutcome = (eventType) =>
  !!eventType && (
    eventType.includes('FAILED') ||
    eventType.includes('MISMATCH') ||
    eventType.includes('ERROR') ||
    eventType.includes('RATE_LIMIT') ||
    eventType.includes('BLOCKED') ||
    eventType.includes('UNAUTHORIZED') ||
    eventType.includes('INVALID') ||
    eventType.includes('EXPIRED') ||
    eventType.includes('DENIED') ||
    eventType.includes('EXCEEDED') ||
    eventType.includes('SUSPICIOUS') ||
    eventType.includes('ABUSE') ||
    eventType.includes('ATTACK')
  );

// Region follows functions/index.js setGlobalOptions (same helper as the
// Maintenance tab): second label of a 4-label databaseURL, else us-central1.
const directFunctionRegion = (config) => {
  const parts = String((config && config.databaseURL) || '').replace(/^https?:\/\//, '').split('/')[0].split('.');
  return parts.length === 4 ? parts[1] : 'us-central1';
};

// A refused or failed start answers within seconds. A dropped connection
// (browser or proxy timeout) or a gateway page after this long means the
// cleanup run had started, and it goes on on the server.
const CLEANUP_STARTED_AFTER_MS = 55000;

// A tool dialog: an icon on a tinted circle, a title, the words, a result.
function ToolDialog({ open, onClose, icon, hue, title, children, actions, closeDisabled }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = `sec-tool-${String(title).replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <Dialog open={open} onClose={closeDisabled ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={phone} aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <IconCircle icon={icon} hue={hue} size={34} />
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}

export default function SecurityLogs() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const { database, auth: firebaseAuth, config } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settingsData = useSelector((state) => state.settingsdata);
  const isAdmin = auth?.profile?.usertype === 'admin';
  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  const [securityLogs, setSecurityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Purge / cleanup / backfill tools: they live with the Logs tab because
  // that is where an admin most often realises they need them.
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [userCleanupDialogOpen, setUserCleanupDialogOpen] = useState(false);
  const [userCleaning, setUserCleaning] = useState(false);
  const [userCleanupResult, setUserCleanupResult] = useState(null);
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;
    const fetchLogs = async () => {
      const logsQuery = query(ref(database, 'security_logs'), limitToLast(100));
      const snapshot = await get(logsQuery);
      const data = snapshot.val();
      if (cancelled) return;
      if (data) {
        const logsArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        logsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setSecurityLogs(logsArray);
      } else {
        setSecurityLogs([]);
      }
      setLoading(false);
    };
    fetchLogs();
    return () => { cancelled = true; };
  }, [database, isAdmin]);

  // The function host, by the same rule the old dashboard used.
  const getFunctionHost = () => {
    const settings = settingsData?.settings;
    return (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
      ? window.location.origin
      : `https://${config?.projectId || 'donkey-drive-5cdd1'}.web.app`;
  };

  const handlePurgeLogs = async () => {
    setPurging(true);
    setPurgeResult(null);
    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) {
        setPurgeResult({ error: 'Not authenticated' });
        setPurging(false);
        return;
      }
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${getFunctionHost()}/purgeSecurityLogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });
      const result = await response.json();
      if (response.ok) {
        setPurgeResult({ success: true, message: result.message });
      } else {
        setPurgeResult({ error: result.error || 'Purge failed' });
      }
    } catch (error) {
      console.error('Purge failed:', error);
      setPurgeResult({ error: error.message });
    } finally {
      setPurging(false);
    }
  };

  const handleUserCleanup = async () => {
    setUserCleaning(true);
    setUserCleanupResult(null);
    let sentAt = 0;
    const startedLongAgo = () => sentAt > 0 && Date.now() - sentAt >= CLEANUP_STARTED_AFTER_MS;
    const stillRunning = {
      pending: true,
      message: L(
        'sec_cleanup_still_running',
        'The connection dropped before the run finished. The run is probably still going on the server (it can take up to 9 minutes), so do not run it again yet. When it ends, its counts are in the triggerUserDataCleanup function log ("manual run complete") and in the newest USER_DATA_CLEANUP_COMPLETED entry of /security_logs in the Firebase console.'
      ),
    };
    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) {
        setUserCleanupResult({ error: 'Not authenticated' });
        setUserCleaning(false);
        return;
      }
      const idToken = await currentUser.getIdToken();
      const projectId = config?.projectId || 'donkey-drive-5cdd1';
      sentAt = Date.now();
      const response = await fetch(`https://${directFunctionRegion(config)}-${projectId}.cloudfunctions.net/triggerUserDataCleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });
      // A gateway answer (502 / 503 / 504, an HTML body) is not the
      // function's reply; after a long wait the run is still going.
      let result = null;
      try { result = await response.json(); } catch (_) { result = null; }
      const gatewayStatus = response.status === 502 || response.status === 503 || response.status === 504;
      if (response.ok && result) {
        setUserCleanupResult({ success: true, data: result });
      } else if (result && !gatewayStatus) {
        setUserCleanupResult({ error: result.error || 'Cleanup failed' });
      } else if (startedLongAgo()) {
        setUserCleanupResult(stillRunning);
      } else {
        setUserCleanupResult({ error: `No reply from the cleanup function (HTTP ${response.status})` });
      }
    } catch (error) {
      console.error('User data cleanup failed:', error);
      // fetch also rejects when the connection drops in the middle of a run.
      setUserCleanupResult(startedLongAgo()
        ? stillRunning
        : { error: `${error.message} (network problem, or this portal address is not allowed by the function)` });
    } finally {
      setUserCleaning(false);
    }
  };

  const handleBackfillStats = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) {
        setBackfillResult({ error: 'Not authenticated' });
        setBackfilling(false);
        return;
      }
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${getFunctionHost()}/backfillTransactionStats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });
      const result = await response.json();
      if (response.ok) {
        setBackfillResult({ success: true, data: result });
        if (typeof window !== 'undefined' && window.__invalidateTransactionStatsCache) {
          window.__invalidateTransactionStatsCache();
        }
      } else {
        setBackfillResult({ error: result.error || 'Backfill failed' });
      }
    } catch (error) {
      console.error('Backfill transaction stats failed:', error);
      setBackfillResult({ error: error.message });
    } finally {
      setBackfilling(false);
    }
  };

  const rows = securityLogs.filter((log) => isNegativeOutcome(log.eventType));

  const openLog = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };
  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedLog(null);
  };

  if (!isAdmin) {
    return <InlineMessage kind="warning">{L('admin_access_required', 'Admin access required.')}</InlineMessage>;
  }

  const severityWord = (severity) =>
    severity === 'high' ? L('sec_severity_high', 'High') : severity === 'medium' ? L('sec_severity_medium', 'Medium') : L('sec_severity_low', 'Low');
  const severityChip = (eventType) => {
    const severity = getEventSeverity(eventType);
    if (severity === 'high') return <StatusChip tone="error" icon={<ReportGmailerrorredRoundedIcon />} label={severityWord(severity)} />;
    if (severity === 'medium') return <StatusChip tone="warning" icon={<WarningAmberRoundedIcon />} label={severityWord(severity)} />;
    return <StatusChip tone="success" icon={<InfoOutlinedIcon />} label={severityWord(severity)} />;
  };

  const unknown = L('unknown', 'Unknown');
  const columns = [
    { title: calmCaps(L('time', 'Time')), field: 'timestamp', type: 'datetime', defaultSort: 'desc' },
    { title: L('sec_col_event', 'Event type'), field: 'eventType', render: (log) => <Box component="span" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{log.eventType || unknown}</Box> },
    {
      title: L('sec_col_severity', 'Severity'),
      id: 'severity',
      sortValue: (log) => ({ high: 3, medium: 2, low: 1 }[getEventSeverity(log.eventType)]),
      searchValue: (log) => severityWord(getEventSeverity(log.eventType)),
      exportValue: (log) => severityWord(getEventSeverity(log.eventType)),
      render: (log) => severityChip(log.eventType),
    },
    {
      title: L('sec_col_ip', 'IP address'),
      id: 'ip',
      sortValue: (log) => log.details?.ip || log.details?.key || '',
      exportValue: (log) => log.details?.ip || log.details?.key || '',
      render: (log) => <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{log.details?.ip || log.details?.key || '—'}</Box>,
    },
  ];

  // Search: event type, IP, mobile or identifier (as before).
  const searchFn = (log, q, ql) =>
    (log.eventType && log.eventType.toLowerCase().includes(ql)) ||
    (log.details?.ip && log.details.ip.toLowerCase().includes(ql)) ||
    (log.details?.mobile && String(log.details.mobile).toLowerCase().includes(ql)) ||
    (log.details?.key && String(log.details.key).toLowerCase().includes(ql));

  const actions = [{ key: 'view', label: L('view_details', 'View details'), icon: <VisibilityOutlinedIcon />, inline: true, onClick: openLog }];
  const title = L('sec_logs_title', 'Security logs');
  const closePurge = () => { setPurgeDialogOpen(false); setPurgeResult(null); };
  const closeCleanup = () => { setUserCleanupDialogOpen(false); setUserCleanupResult(null); };
  const closeBackfill = () => { setBackfillDialogOpen(false); setBackfillResult(null); };
  const d = selectedLog && selectedLog.details ? selectedLog.details : {};

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        secondaryActions={[
          { key: 'purge', label: L('sec_purge', 'Purge logs'), icon: <DeleteSweepOutlinedIcon />, onClick: () => setPurgeDialogOpen(true) },
          { key: 'cleanup', label: L('sec_user_cleanup', 'User cleanup'), icon: <CleaningServicesOutlinedIcon />, onClick: () => setUserCleanupDialogOpen(true) },
          { key: 'backfill', label: L('sec_backfill', 'Backfill stats'), icon: <AssessmentOutlinedIcon />, onClick: () => setBackfillDialogOpen(true) },
        ]}
      />
      <DataTable
        title={title}
        rows={rows}
        columns={columns}
        loading={loading}
        countText={({ loading: busy, shown, total, query: q }) =>
          busy
            ? L('loading', 'Loading…')
            : q
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : L('sec_logs_count', '{{count}} events', { count: total, defaultValue_one: '{{count}} event', defaultValue_other: '{{count}} events' })
        }
        subtitle={L('sec_logs_subtitle', 'Failed, blocked and suspicious events from the latest 100 log entries')}
        searchPlaceholder={L('sec_logs_search', 'Search event type, IP or mobile')}
        searchFn={searchFn}
        onRowClick={openLog}
        rowLabel={(log) => log.eventType || unknown}
        actions={actions}
        prefsKey="security_logs"
        minWidth={720}
        emptyState={({ query: q }) => (q ? null : { icon: <ShieldOutlinedIcon />, title: L('sec_logs_empty', 'No security logs found') })}
      />

      {/* Event details */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetail} maxWidth="sm" fullWidth fullScreen={phone} aria-labelledby="sec-log-title">
        <DialogTitle id="sec-log-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 8 }}>
          <IconCircle icon={<GppMaybeOutlinedIcon />} hue="danger" size={34} />
          {L('sec_event_details', 'Security event details')}
          <IconButton aria-label={L('close_dialog', 'Close')} onClick={handleCloseDetail} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog ? (
            <Box component="dl" sx={{ m: 0 }}>
              <InfoRow label={L('sec_col_event', 'Event type')}>
                <Box component="span" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{selectedLog.eventType || unknown}</Box>
              </InfoRow>
              <InfoRow label={L('sec_col_severity', 'Severity')}>{severityChip(selectedLog.eventType)}</InfoRow>
              <InfoRow label={calmCaps(L('time', 'Time'))}>{selectedLog.timestamp ? formatAddisDateTime(selectedLog.timestamp) : null}</InfoRow>
              {d.ip ? <InfoRow label={L('sec_col_ip', 'IP address')}>{d.ip}</InfoRow> : null}
              {d.key ? <InfoRow label={L('sec_identifier', 'Identifier')}>{d.key}</InfoRow> : null}
              {d.mobile ? <InfoRow label={L('mobile', 'Mobile number')}>{d.mobile}</InfoRow> : null}
              {d.email ? <InfoRow label={L('email', 'Email')}>{d.email}</InfoRow> : null}
              {d.type ? <InfoRow label={L('sec_rate_limit_type', 'Rate limit type')}>{d.type}</InfoRow> : null}
              {d.requestCount ? <InfoRow label={L('sec_request_count', 'Request count')}>{d.requestCount}</InfoRow> : null}
              {d.limit ? <InfoRow label={L('sec_limit', 'Limit')}>{d.limit}</InfoRow> : null}
              {d.reason ? <InfoRow label={L('reason', 'Reason')} alignTop>{d.reason}</InfoRow> : null}
              {selectedLog.date ? <InfoRow label={L('sec_date_iso', 'Date (ISO)')}><Box component="span" sx={{ color: c.TEXT_3 }}>{selectedLog.date}</Box></InfoRow> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDetail}>
            {L('close_dialog', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purge */}
      <ToolDialog
        open={purgeDialogOpen}
        onClose={closePurge}
        closeDisabled={purging}
        icon={<DeleteSweepOutlinedIcon />}
        hue="danger"
        title={L('sec_purge_title', 'Purge security logs')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={closePurge} disabled={purging}>
              {L('cancel', 'Cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={handlePurgeLogs} disabled={purging} startIcon={purging ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepOutlinedIcon />}>
              {purging ? L('sec_purging', 'Purging…') : L('sec_purge_now', 'Purge now')}
            </Button>
          </>
        }
      >
        <Typography sx={{ mb: 1.5 }}>{L('sec_purge_body', 'This permanently deletes ALL security logs.')}</Typography>
        <Typography variant="body2" sx={{ color: c.INK_2 }}>
          {L('sec_purge_note', 'Automatic cleanup runs daily at 3:00 am and removes logs older than 7 days.')}
        </Typography>
        {purgeResult ? (
          <InlineMessage kind={purgeResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {purgeResult.success ? purgeResult.message : purgeResult.error}
          </InlineMessage>
        ) : null}
      </ToolDialog>

      {/* User data cleanup */}
      <ToolDialog
        open={userCleanupDialogOpen}
        onClose={closeCleanup}
        closeDisabled={userCleaning}
        icon={<CleaningServicesOutlinedIcon />}
        hue="amber"
        title={L('sec_cleanup_title', 'User data cleanup')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={closeCleanup} disabled={userCleaning}>
              {L('close_dialog', 'Close')}
            </Button>
            <Button
              variant="contained"
              onClick={handleUserCleanup}
              disabled={userCleaning || !!userCleanupResult?.pending}
              startIcon={userCleaning ? <CircularProgress size={16} color="inherit" /> : <CleaningServicesOutlinedIcon />}
            >
              {userCleaning ? L('sec_running', 'Running…') : L('sec_run_cleanup', 'Run cleanup')}
            </Button>
          </>
        }
      >
        <Typography sx={{ mb: 1 }}>{L('sec_cleanup_body', 'For every user, this deletes:')}</Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2, color: c.INK_2 }}>
          <li>{L('sec_cleanup_item_notifications', '/userNotifications entries older than 7 days')}</li>
          <li>{L('sec_cleanup_item_addresses', '/users/{uid}/savedAddresses, the retired auto-saved trip places (named Home / Work places are kept)')}</li>
        </Box>
        <Typography variant="body2" sx={{ color: c.INK_2 }}>
          {L('sec_cleanup_note', 'The scheduled job runs every Sunday at 8:00 pm Addis Ababa time. Use this button to run it now. A run takes a few minutes; keep this dialog open to see its counts.')}
        </Typography>
        {userCleanupResult ? (
          <InlineMessage
            kind={userCleanupResult.success ? 'success' : userCleanupResult.pending ? 'info' : 'error'}
            title={userCleanupResult.success ? L('sec_cleanup_done', 'Cleanup complete') : undefined}
            sx={{ mt: 2 }}
          >
            {userCleanupResult.success ? (
              <Box component="dl" sx={{ m: 0, '& dt': { color: 'inherit' }, '& dd': { color: 'inherit' } }}>
                <InfoRow label={L('sec_users_scanned', 'Users scanned')}>{userCleanupResult.data?.usersScanned ?? 0}</InfoRow>
                <InfoRow label={L('sec_notifications_deleted', 'Notifications deleted')}>
                  {L('sec_notifications_across', '{{n}} across {{users}} users', { n: userCleanupResult.data?.notificationsDeleted ?? 0, users: userCleanupResult.data?.notifUsersAffected ?? 0 })}
                </InfoRow>
                <InfoRow label={L('sec_addresses_cleared', 'Auto-saved addresses deleted for')}>
                  {L('sec_addresses_users', '{{n}} users (not read first, so users who had none are counted too)', { n: userCleanupResult.data?.addrUsersCleared ?? 0 })}
                </InfoRow>
                {userCleanupResult.data?.usersDoneEarlier > 0 ? (
                  <InfoRow label={L('sec_carried_on', 'Carried on from the last run')}>
                    {L('sec_users_done', '{{n}} users were already done', { n: userCleanupResult.data.usersDoneEarlier })}
                  </InfoRow>
                ) : null}
                {userCleanupResult.data?.usersNotReached > 0 ? (
                  <InfoRow label={L('sec_not_reached', 'Not reached before the time limit')}>
                    {L('sec_not_reached_users', '{{n}} users. Run it again to carry on from there.', { n: userCleanupResult.data.usersNotReached })}
                  </InfoRow>
                ) : null}
                {userCleanupResult.data?.durationMs != null ? (
                  <InfoRow label={L('duration', 'Duration')}>{`${Math.round(userCleanupResult.data.durationMs / 1000)} s`}</InfoRow>
                ) : null}
                {userCleanupResult.data?.chunkErrors > 0 ? (
                  <InfoRow label={L('sec_chunk_errors', 'Chunk errors')}>
                    {L('sec_chunk_errors_value', '{{n}} (check the function logs)', { n: userCleanupResult.data.chunkErrors })}
                  </InfoRow>
                ) : null}
              </Box>
            ) : userCleanupResult.pending ? (
              userCleanupResult.message
            ) : (
              userCleanupResult.error
            )}
          </InlineMessage>
        ) : null}
      </ToolDialog>

      {/* Backfill /transactionStats */}
      <ToolDialog
        open={backfillDialogOpen}
        onClose={closeBackfill}
        closeDisabled={backfilling}
        icon={<AssessmentOutlinedIcon />}
        hue="info"
        title={L('sec_backfill_title', 'Backfill transaction stats')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={closeBackfill} disabled={backfilling}>
              {L('close_dialog', 'Close')}
            </Button>
            <Button variant="contained" onClick={handleBackfillStats} disabled={backfilling} startIcon={backfilling ? <CircularProgress size={16} color="inherit" /> : <AssessmentOutlinedIcon />}>
              {backfilling ? L('sec_backfilling', 'Backfilling…') : L('sec_run_backfill', 'Run backfill')}
            </Button>
          </>
        }
      >
        <Typography sx={{ mb: 1 }}>
          {L('sec_backfill_body', 'This reads every entry in /walletHistory, adds them up by day, source and type, and overwrites /transactionStats with the fresh result.')}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2, color: c.INK_2 }}>
          <li>{L('sec_backfill_item_seed', 'Use after deploying the stats feature to seed historical totals.')}</li>
          <li>{L('sec_backfill_item_wrong', 'Use any time the Transaction logs totals look wrong.')}</li>
          <li>{L('sec_backfill_item_idempotent', 'Safe to re-run: running it again gives the same result.')}</li>
        </Box>
        <Typography variant="body2" sx={{ color: c.INK_2 }}>
          {L('sec_backfill_caveat', 'Caveat: wallet writes during the run (seconds) may be overwritten. Run it during quiet hours, or run it again to catch up.')}
        </Typography>
        {backfillResult ? (
          <InlineMessage kind={backfillResult.success ? 'success' : 'error'} title={backfillResult.success ? L('sec_backfill_done', 'Backfill complete') : undefined} sx={{ mt: 2 }}>
            {backfillResult.success ? (
              <Box component="dl" sx={{ m: 0, '& dt': { color: 'inherit' }, '& dd': { color: 'inherit' } }}>
                <InfoRow label={L('sec_entries_processed', 'Entries processed')}>{backfillResult.data?.entriesProcessed ?? 0}</InfoRow>
                {backfillResult.data?.entriesSkipped > 0 ? (
                  <InfoRow label={L('sec_entries_skipped', 'Entries skipped (malformed)')}>{backfillResult.data.entriesSkipped}</InfoRow>
                ) : null}
                <InfoRow label={L('sec_days_aggregated', 'Days added up')}>{backfillResult.data?.daysAggregated ?? 0}</InfoRow>
                {backfillResult.data?.elapsedMs != null ? (
                  <InfoRow label={L('duration', 'Duration')}>{`${(backfillResult.data.elapsedMs / 1000).toFixed(1)} s`}</InfoRow>
                ) : null}
              </Box>
            ) : (
              backfillResult.error
            )}
          </InlineMessage>
        ) : null}
      </ToolDialog>
    </Box>
  );
}
